import {useEffect, useRef} from "react";

/**
 * Reusable hook that checks whether a click has come from outside the given DOM container
 * @author Muhammad Shahrukh <shahrukh@250mils.com>
 * @param {React.Ref} containerToCheckRef the Ref that for the DOM element for which we need to check whether there has been a click outside it or not.
 *                           defaults to the document
 * @param {function} onClickedOutside callback to execute once a click outside has been registered
 * @param {Element|Array<Element|null>|null} excludeFromCheck dom element(s) outside the container that should be excluded from the check
 * @param {function|null} setHandlerCondition Optional. A function if true, sets the handler. returns otherwise.
 * */
const useOutsideClick = (containerToCheckRef, onClickedOutside, excludeFromCheck = null, setHandlerCondition = null) => {
    const shouldSetHandler = typeof setHandlerCondition === 'function' ? setHandlerCondition(): true;

    const excludeFromCheckRef = useRef(excludeFromCheck);
    excludeFromCheckRef.current = excludeFromCheck;

    /**
     *
     * @param {Event} e
     * @returns {boolean}
     */
    const isClickFromExcludedElement = (e) => {
        const excludeValue = excludeFromCheckRef.current;
        if (excludeValue === null) {
            return false;
        }
        const excludedElements = Array.isArray(excludeValue) ? excludeValue : [excludeValue];
        return excludedElements.some((element) => element !== null && element.contains(e.target));
    }

    /**
     *
     * @param {Event} e
     * @returns {boolean}
     */
    const isClickFromOutsideContainer = (e) => {
        if (containerToCheckRef.current) {
            return (!containerToCheckRef.current.contains(e.target) && !isClickFromExcludedElement(e))
        }

        return false;
    }

    useEffect(() => {
        if (!shouldSetHandler) {
            return;
        }

        /**
         *
         * @param {Event} e
         */
        const handler = (e) => {
            if (isClickFromOutsideContainer(e) && typeof onClickedOutside === 'function') {
                onClickedOutside(e);
            }
        }

        document.addEventListener('mousedown', handler, {capture: true});

        return () => {
            document.removeEventListener('mousedown', handler, {capture: true});
        }
    }, [onClickedOutside, shouldSetHandler]);
}

export default useOutsideClick;